import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AccountBalanceWallet,
  Lock,
  Person,
  Security,
  Settings,
  Save,
  Visibility,
  VisibilityOff,
  Email,
  Phone,
  HelpOutline,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ButtonTabs, { type ButtonTab } from '../components/ButtonTabs';
import DetailHeader from '../components/DetailHeader';
import SubscriptionOverview from '../components/SubscriptionOverview';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useLanguage } from '../hooks/useLanguage';
import { useThemeMode } from '../contexts/ThemeModeContext';
import usePageTitle from '../hooks/usePageTitle';
import type { SubscriptionPlanType } from '../types';
import AccountTenantSettingsTab from '../components/AccountTenantSettingsTab';
import PublicApiCredentialsManager from '../components/PublicApiCredentialsManager';
import { getApiErrorMessage } from '../utils/errors';

interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface SessionItem {
  id: string;
  ipAddress: string | null;
  userAgent: string;
  isActive: boolean;
  lastActivity: string | null;
  createdAt: string;
  expiresAt: string;
}

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
};

export default function AccountPage() {
  const { t } = useTranslation(['pages', 'common']);
  usePageTitle(t('pages:account.title'));

  const { language, setLanguage, availableLanguages } = useLanguage();
  const { mode, setMode } = useThemeMode();
  const { capabilities, subscriptions, user } = useAuth();
  const { hasPermission } = usePermissions();
  const canReadTenantSettings = hasPermission('tenant_settings_read');
  const canReadPublicApiCredentials = hasPermission('public_api_credentials_read');
  const canReadCurrentSubscriptions = hasPermission('subscriptions_current_read');
  const hasPublicApiEnabled = Boolean(capabilities?.hasPublicApiEnabled);

  const [profile, setProfile] = useState<ProfileData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [languageSuccess, setLanguageSuccess] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [sessions, setSessions] = useState<SessionItem[]>([]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoadingProfile(true);
        const response = await api.get('/api/account/profile/');
        setProfile({
          firstName: response.data.first_name || response.data.firstName || '',
          lastName: response.data.last_name || response.data.lastName || '',
          email: response.data.email || '',
          phone: response.data.phone || '',
        });
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setLoadingSessions(true);
        const response = await api.get('/api/account/sessions/');
        setSessions(response.data.sessions || []);
      } finally {
        setLoadingSessions(false);
      }
    };

    fetchSessions();
  }, []);

  const handleSaveProfile = useCallback(async () => {
    setSavingProfile(true);
    setProfileError('');
    setProfileSuccess(false);

    try {
      await api.put('/api/account/profile/', {
        first_name: profile.firstName,
        last_name: profile.lastName,
        email: profile.email,
        phone: profile.phone,
      });
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err: unknown) {
      setProfileError(getApiErrorMessage(err, t('pages:profile.errorSaving')));
    } finally {
      setSavingProfile(false);
    }
  }, [profile, t]);

  const handleChangePassword = useCallback(async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(t('pages:profile.allFieldsRequired'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t('pages:profile.passwordsDontMatch'));
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError(t('pages:profile.passwordTooShort'));
      return;
    }

    setSavingPassword(true);
    setPasswordError('');
    setPasswordSuccess(false);

    try {
      await api.put('/api/account/password/', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err: unknown) {
      setPasswordError(getApiErrorMessage(err, t('pages:profile.errorChangingPassword')));
    } finally {
      setSavingPassword(false);
    }
  }, [confirmPassword, currentPassword, newPassword, t]);

  const handleLanguageChange = useCallback(async (newLanguage: string) => {
    try {
      await setLanguage(newLanguage as 'en' | 'pt-BR' | 'es');
      setLanguageSuccess(true);
      setTimeout(() => setLanguageSuccess(false), 3000);
    } catch (err) {
      console.error('Error changing language:', err);
    }
  }, [setLanguage]);

  const getInitials = () => {
    const first = profile.firstName?.charAt(0) || '';
    const last = profile.lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U';
  };

  const hasAiSubscription = Boolean(subscriptions?.ai?.hasActive);
  const hasReverseIvrSubscription = Boolean(subscriptions?.reverseIvr?.hasActive);
  const subscriptionTypes = useMemo<SubscriptionPlanType[]>(() => {
    const activeTypes: SubscriptionPlanType[] = [];
    if (hasAiSubscription) activeTypes.push('ai');
    if (hasReverseIvrSubscription) activeTypes.push('reverse_ivr');
    return activeTypes;
  }, [hasAiSubscription, hasReverseIvrSubscription]);

  const subscriptionTabContent = useMemo(() => {
    if (subscriptionTypes.length <= 1) {
      const singleType = subscriptionTypes[0];
      return <SubscriptionOverview showHeader={false} subscriptionType={singleType} />;
    }

    const typeTabs: ButtonTab[] = [
      {
        id: 'ai',
        label: t('pages:subscription.planTypes.ai'),
        content: <SubscriptionOverview showHeader={false} subscriptionType="ai" />,
      },
      {
        id: 'reverse_ivr',
        label: t('pages:subscription.planTypes.reverse_ivr'),
        content: <SubscriptionOverview showHeader={false} subscriptionType="reverse_ivr" />,
      },
    ];

    return (
      <ButtonTabs
        tabs={typeTabs}
        defaultTab={subscriptionTypes[0] ?? 'ai'}
        useUrl
        tabParam="subscription_type"
        basePath="/account"
      />
    );
  }, [
    subscriptionTypes,
    t,
  ]);

  const settingsTabs = useMemo<ButtonTab[]>(() => {
    const resolvedTabs: ButtonTab[] = [];

    if (canReadTenantSettings) {
      resolvedTabs.push(
        {
          id: 'organization',
          label: t('pages:account.settingsTabs.organization'),
          content: <AccountTenantSettingsTab section="organization" />,
        },
        {
          id: 'operating_hours',
          label: t('pages:account.settingsTabs.operatingHoursAvailability'),
          content: <AccountTenantSettingsTab section="operatingHours" />,
        },
        {
          id: 'music_on_hold',
          label: t('pages:account.settingsTabs.musicOnHold'),
          content: <AccountTenantSettingsTab section="music" />,
        },
      );
    }

    if (canReadPublicApiCredentials && hasPublicApiEnabled) {
      resolvedTabs.push({
        id: 'public_api',
        label: t('pages:account.settingsTabs.publicApi'),
        content: <PublicApiCredentialsManager mode="account" />,
      });
    }

    return resolvedTabs;
  }, [canReadPublicApiCredentials, canReadTenantSettings, hasPublicApiEnabled, t]);

  const tabs = useMemo<ButtonTab[]>(
    () => [
      {
        id: 'profile',
        label: t('pages:account.tabs.profile'),
        icon: <Person />,
        content: (
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Person color="primary" />
                  <Typography variant="h6">{t('pages:profile.personalInfo')}</Typography>
                </Box>

                {profileSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {t('pages:profile.profileSaved')}
                  </Alert>
                )}
                {profileError && (
                  <Alert severity="error" sx={{ mb: 2 }}>{profileError}</Alert>
                )}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                  <TextField
                    label={t('pages:profile.firstName')}
                    value={profile.firstName}
                    onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                    fullWidth
                  />
                  <TextField
                    label={t('pages:profile.lastName')}
                    value={profile.lastName}
                    onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                    fullWidth
                  />
                  <TextField
                    label={t('pages:profile.email')}
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    fullWidth
                    type="email"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label={t('pages:profile.phone')}
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={savingProfile ? <CircularProgress size={20} color="inherit" /> : <Save />}
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                  >
                    {savingProfile ? t('common:saving') : t('pages:profile.saveProfile')}
                  </Button>
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Person color="primary" />
                  <Typography variant="h6">{t('pages:profile.preferences')}</Typography>
                </Box>

                {languageSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {t('common:language.changed')}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <FormControl sx={{ minWidth: 240 }}>
                    <InputLabel>{t('pages:profile.interfaceLanguage')}</InputLabel>
                    <Select
                      value={language}
                      label={t('pages:profile.interfaceLanguage')}
                      onChange={(e) => handleLanguageChange(e.target.value)}
                    >
                      {availableLanguages.map((lang) => (
                        <MenuItem key={lang.code} value={lang.code}>
                          {lang.nativeName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl sx={{ minWidth: 240 }}>
                      <InputLabel>{t('common:theme.label')}</InputLabel>
                      <Select
                        value={mode}
                        label={t('common:theme.label')}
                        onChange={(e) => setMode(e.target.value as 'dark' | 'light')}
                      >
                        <MenuItem value="dark">{t('common:theme.dark')}</MenuItem>
                        <MenuItem value="light">{t('common:theme.light')}</MenuItem>
                      </Select>
                    </FormControl>
                    <Tooltip title={t('pages:profile.themeTooltip')} arrow>
                      <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Stack>
        ),
      },
      ...(canReadCurrentSubscriptions
        ? [{
            id: 'subscription',
            label: t('pages:account.tabs.subscriptions'),
            icon: <AccountBalanceWallet />,
            content: subscriptionTabContent,
          }]
        : []),
      ...((canReadTenantSettings || (canReadPublicApiCredentials && hasPublicApiEnabled))
        ? [
            {
              id: 'settings',
              label: t('pages:account.tabs.settings'),
              icon: <Settings />,
              content: (
                <ButtonTabs
                  tabs={settingsTabs}
                  defaultTab={settingsTabs[0]?.id || 'organization'}
                  useUrl
                  tabParam="settings_tab"
                  basePath="/account"
                />
              ),
            },
          ]
        : []),
      {
        id: 'security',
        label: t('pages:account.tabs.security'),
        icon: <Security />,
        content: (
          <ButtonTabs
            tabs={[
              {
                id: 'password',
                label: t('pages:account.securityTabs.password'),
                content: (
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <Lock color="primary" />
                        <Typography variant="h6">{t('pages:profile.changePassword')}</Typography>
                      </Box>

                      {passwordSuccess && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          {t('pages:profile.passwordChanged')}
                        </Alert>
                      )}
                      {passwordError && (
                        <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>
                      )}

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 420 }}>
                        <TextField
                          label={t('pages:profile.currentPassword')}
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          fullWidth
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                  edge="end"
                                >
                                  {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Divider />
                        <TextField
                          label={t('pages:profile.newPassword')}
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          fullWidth
                          helperText={t('pages:profile.passwordMinLength')}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowNewPassword(!showNewPassword)}
                                  edge="end"
                                >
                                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          label={t('pages:profile.confirmPassword')}
                          type={showNewPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          fullWidth
                          error={confirmPassword !== '' && confirmPassword !== newPassword}
                          helperText={confirmPassword !== '' && confirmPassword !== newPassword ? t('pages:profile.passwordsDontMatch') : ''}
                        />
                      </Box>

                      <Box sx={{ mt: 3 }}>
                        <Button
                          variant="contained"
                          color="warning"
                          startIcon={savingPassword ? <CircularProgress size={20} color="inherit" /> : <Lock />}
                          onClick={handleChangePassword}
                          disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword}
                        >
                          {savingPassword ? t('common:saving') : t('pages:profile.updatePassword')}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ),
              },
              {
                id: 'sessions',
                label: t('pages:account.securityTabs.sessions'),
                content: (
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <Security color="primary" />
                        <Typography variant="h6">{t('pages:account.sessions.title')}</Typography>
                      </Box>

                      {loadingSessions ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                          <CircularProgress />
                        </Box>
                      ) : sessions.length === 0 ? (
                        <Typography color="text.secondary">{t('pages:account.sessions.empty')}</Typography>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>{t('pages:account.sessions.ip')}</TableCell>
                                <TableCell>{t('pages:account.sessions.device')}</TableCell>
                                <TableCell>{t('pages:account.sessions.lastActivity')}</TableCell>
                                <TableCell>{t('pages:account.sessions.status')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sessions.map((session) => (
                                <TableRow key={session.id}>
                                  <TableCell>{session.ipAddress || '-'}</TableCell>
                                  <TableCell sx={{ maxWidth: 320 }}>
                                    <Typography variant="body2" noWrap title={session.userAgent || '-'}>
                                      {session.userAgent || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{formatDateTime(session.lastActivity)}</TableCell>
                                  <TableCell>
                                    {session.isActive
                                      ? t('pages:account.sessions.active')
                                      : t('pages:account.sessions.inactive')}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </CardContent>
                  </Card>
                ),
              },
            ]}
            defaultTab="password"
            useUrl
            tabParam="security_tab"
            basePath="/account"
          />
        ),
      },
    ],
    [
      t,
      profile,
      profileSuccess,
      profileError,
      savingProfile,
      language,
      availableLanguages,
      languageSuccess,
      mode,
      currentPassword,
      newPassword,
      confirmPassword,
      showCurrentPassword,
      showNewPassword,
      passwordSuccess,
      passwordError,
      savingPassword,
      sessions,
      loadingSessions,
      subscriptionTabContent,
      canReadCurrentSubscriptions,
      canReadPublicApiCredentials,
      canReadTenantSettings,
      hasPublicApiEnabled,
      settingsTabs,
      handleChangePassword,
      handleLanguageChange,
      handleSaveProfile,
      setMode,
    ],
  );

  if (loadingProfile) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <DetailHeader
        title={t('pages:account.title')}
        subtitle={t('pages:account.subtitle')}
        sectionIcon={<Person />}
        breadcrumbs={[{ label: t('pages:account.title') }]}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 18,
            }}
          >
            {getInitials()}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {profile.firstName || profile.lastName
                ? `${profile.firstName} ${profile.lastName}`.trim()
                : t('pages:account.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {profile.email}
            </Typography>
          </Box>
        </Box>
      </Box>

      <ButtonTabs
        tabs={tabs}
        defaultTab="profile"
        useUrl
        tabParam="tab"
        basePath="/account"
      />
    </Box>
  );
}
